import {z} from 'zod';

export const CATEGORIAS = [
  'Comida y bebida',
  'Moda',
  'Belleza',
  'Hogar',
  'Servicios',
  'Mascotas',
  'Artesanías',
  'Salud y bienestar',
  'Otro',
] as const;

export const BOOTH_ITEMS = [
  {id: 'mesa', label: 'Mesa (1)', hint: 'aproximadamente 6 pies'},
  {id: 'mantel', label: 'Mantel (1)'},
  {id: 'silla', label: 'Silla (1)'},
  {id: 'ninguno', label: 'Ninguno'},
] as const;

/**
 * Amperajes que puede pedir un comercio para su booth.
 *
 * `value` es el texto EXACTO que viaja a la API en `amperage`: allí el campo es
 * string libre, no enum, así que el backend no valida el contenido y esta lista
 * es el único control. Los valores terminan literales en el reporte del
 * cliente — cambiarlos a mitad de la convocatoria partiría los datos en dos,
 * así que se tratan como fijos.
 */
export const AMPERAJES = [
  {value: '5 Amp', label: '5 Amp'},
  {value: '10 Amp', label: '10 Amp'},
  {value: '20 Amp', label: '20 Amp'},
] as const;

/**
 * Deja solo los dígitos de un teléfono.
 *
 * El registro termina en una hoja de cálculo y allí un valor que empiece por
 * "+" o "-" se lee como fórmula, así que el "+1", los guiones y los espacios
 * se descartan en vez de viajar. Se aplica al escribir (VendorForm) y otra vez
 * al construir el payload, para que nada dependa de una sola capa.
 */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/** Tope de dígitos de un teléfono (E.164), el mismo que exige el esquema. */
export const PHONE_MAX_DIGITS = 15;

export const registrationSchema = z
  .object({
    // Información del negocio
    // Los .max() replican los límites de la API (openapi.yaml) para atajarlos
    // aquí y no con un 400 después de enviar.
    businessName: z
      .string()
      .trim()
      .min(2, 'Escribe el nombre de tu negocio.')
      .max(200, 'El nombre no puede pasar de 200 caracteres.'),
    contactName: z
      .string()
      .trim()
      .min(3, 'Escribe nombre y apellido.')
      .max(200, 'El nombre no puede pasar de 200 caracteres.'),
    role: z.string().trim().min(2, 'Escribe tu cargo.').max(100, 'El cargo no puede pasar de 100 caracteres.'),
    email: z
      .email('Revisa el formato del correo (ej. nombre@negocio.com).')
      .max(254, 'El correo no puede pasar de 254 caracteres.'),
    // Solo dígitos: el registro termina en una hoja de cálculo, y un teléfono
    // que empiece por "+" o "-" se interpreta allí como fórmula. Ni el guion
    // ni el paréntesis aportan nada al dato, así que no se aceptan.
    phone: z
      .string()
      .trim()
      .regex(
        new RegExp(`^\\d{10,${PHONE_MAX_DIGITS}}$`),
        'Escribe el teléfono solo con números, sin guiones ni espacios (ej. 7875550123).',
      ),
    municipio: z.string().min(1, 'Selecciona tu municipio.'),
    category: z.enum(CATEGORIAS, {error: 'Selecciona una categoría.'}),
    categoryOther: z.string().trim().max(100, 'Máximo 100 caracteres.').optional(),

    // ATH Business
    hasAthBusiness: z.enum(['si', 'no'], {
      error: 'Indica si tu negocio está registrado en ATH Business.',
    }),
    pathName: z.string().trim().max(200, 'Máximo 200 caracteres.').optional(),

    // Participación
    participatedBefore: z.enum(['si', 'no'], {
      error: 'Indica si has participado antes.',
    }),

    // Redes sociales
    instagram: z
      .string()
      .trim()
      .min(2, 'Escribe el Instagram de tu negocio (ej. @tunegocio).')
      .max(100, 'Máximo 100 caracteres.'),
    facebook: z.string().trim().max(200, 'Máximo 200 caracteres.').optional(),
    website: z.string().trim().max(500, 'Máximo 500 caracteres.').optional(),

    // Información del espacio
    boothNeeds: z.array(z.string()).min(1, 'Selecciona al menos una opción (o «Ninguno»).'),
    needsElectricity: z.enum(['si', 'no'], {
      error: 'Indica si necesitarás electricidad en tu booth.',
    }),
    // El valor viaja tal cual a la API; el max replica su maxLength de 20.
    amperage: z.string().trim().max(20, 'Máximo 20 caracteres.').optional(),

    // Productos
    products: z
      .string()
      .trim()
      .min(10, 'Cuéntanos qué venderás (mínimo unas palabras).')
      .max(1000, 'Máximo 1000 caracteres.'),

    // Evertec
    isEvertecMerchant: z.enum(['pos', 'otro', 'ninguno'], {
      error: 'Indica qué servicio de Evertec tienes.',
    }),
    merchantId: z.string().trim().max(50, 'Máximo 50 caracteres.').optional(),

    // Confirmación
    confirm: z.literal(true, {
      error: 'Debes confirmar tu compromiso para enviar la solicitud.',
    }),
  })
  .refine((d) => d.category !== 'Otro' || (d.categoryOther ?? '').length > 0, {
    message: 'Especifica la categoría de tu negocio.',
    path: ['categoryOther'],
  })
  // El /pATH solo puede exigirse a quien ya está registrado en ATH Business.
  .refine((d) => d.hasAthBusiness !== 'si' || (d.pathName ?? '').length > 0, {
    message: 'Escribe el nombre de tu negocio en ATH Business.',
    path: ['pathName'],
  })
  // "Otro" es la única opción con texto libre; la API exige merchant_id cuando
  // is_evertec_merchant = true, y ese campo es el que transporta la respuesta.
  .refine((d) => d.isEvertecMerchant !== 'otro' || (d.merchantId ?? '').length > 0, {
    message: 'Escribe cuál servicio de Evertec tienes.',
    path: ['merchantId'],
  })
  // El contrato exige amperage siempre que needs_electricity sea true.
  .refine((d) => d.needsElectricity !== 'si' || (d.amperage ?? '').length > 0, {
    message: 'Selecciona el amperaje que necesitas.',
    path: ['amperage'],
  });

export type VendorRegistration = z.infer<typeof registrationSchema>;
